'use client';

import { MarketType, OrderType } from '@drift-labs/sdk';
import { ENUM_UTILS } from '@drift/common';
import TextButton from 'src/components/primitives/TextButton/TextButton';
import Divider from 'src/components/primitives/Divider/Divider';
import Tooltip from 'src/components/primitives/Tooltip/Tooltip';
import TableActionButton from 'src/components/Buttons/TableActionButton';
import useModal from 'src/components/Modals/hooks/useModal';
import useDevSwitchIsOn from 'src/hooks/useDevSwitchIsOn';
import { useCancelOrders } from 'src/hooks/trade/cancel/useCancelOrders';
import { useFillOrder } from 'src/hooks/driftActions/order/useFillOrder';
import { useTriggerOrder } from 'src/hooks/driftActions/order/useTriggerOrder';
import { AccountOrder } from '../types';

interface EditCellProps {
	order: AccountOrder;
}

export function EditCell({ order }: EditCellProps) {
	const { show } = useModal();
	const isDevMode = useDevSwitchIsOn();
	const { cancelOrders } = useCancelOrders();
	const { fillOrder } = useFillOrder();
	const { triggerOrder } = useTriggerOrder();

	const isMarketOrder =
		ENUM_UTILS.match(order.orderType, OrderType.MARKET) ||
		ENUM_UTILS.match(order.orderType, OrderType.ORACLE);
	const shouldShowEditButton = !isMarketOrder;

	const isTriggeredOrder =
		ENUM_UTILS.match(order.orderType, OrderType.TRIGGER_MARKET) ||
		ENUM_UTILS.match(order.orderType, OrderType.TRIGGER_LIMIT);
	const showTriggerButton = isDevMode && isTriggeredOrder;

	return (
		<div className="flex items-center justify-center">
			<div className="flex items-center justify-end gap-sm">
				{shouldShowEditButton && (
					<>
						<TextButton
							variant="tertiary"
							size="sm"
							onClick={(e) => {
								e.stopPropagation();
								show({ type: 'edit_order', props: { order } });
							}}
						>
							Edit
						</TextButton>
						<Divider vertical />
					</>
				)}
				<TextButton
					variant="tertiary"
					size="sm"
					onClick={(e) => {
						e.stopPropagation();
						const toastId = `order-row-cancel-order-${order.orderId}-${Date.now()}`;
						cancelOrders(toastId, order.orderId);
					}}
				>
					Cancel
				</TextButton>
			</div>
			{isDevMode && (
				<div className="absolute -top-3 -right-2 flex items-center justify-end gap-1">
					<Tooltip
						content={
							<>
								<p>this is devmode</p>
								{ENUM_UTILS.match(order.marketType, MarketType.PERP) && (
									<TableActionButton
										className="justify-start px-0"
										label="Fill"
										handleClick={() => {
											fillOrder(
												order.orderId,
												order.marketIndex,
												order.direction
											);
										}}
									/>
								)}
								{showTriggerButton &&
									ENUM_UTILS.match(order.marketType, MarketType.PERP) && (
										<TableActionButton
											className="justify-start px-0"
											label="Trigger"
											handleClick={() => {
												triggerOrder(order);
											}}
										/>
									)}
							</>
						}
					>
						<div className="inline-flex h-4 w-4 items-center justify-center rounded-full bg-input-bg text-[10px] leading-none text-text-secondary">
							!
						</div>
					</Tooltip>
				</div>
			)}
		</div>
	);
}
